import {
  EXCLUSIVES_CATEGORY_URL_ID,
  NEW_BRANDS_CATEGORY_URL_ID,
  NEW_PRODUCTS_CATEGORY_URL_ID,
} from "@faire/retailer-shared/categories/urlId";
import { ICategory } from "@faire/web-api/indigofair/data/ICategory";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { IRoutingContext } from "@faire/web/ui/routing/RoutingContext";

import { Category } from "@faire/retailer/models/Category/Category";
import { categoryToUrlId } from "@faire/retailer/models/Category/categoryToUrlId";
import { IUrlId } from "@faire/retailer/models/Category/IUrlId";
import { categoryPath } from "@faire/retailer/paths/category/categoryPath";
import { ILeftNavLink } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/LeftNav/__internal__/ILeftNavLink";

/**
 * Returns the left nav category link tree for a given category
 */
export const getLeftNavLinks = (
  categories: ICategory[],
  selectedCategory: Category,
  routingContext: IRoutingContext
): ILeftNavLink[] => {
  const showNewLeftCategoryNavigation =
    selectedCategory.isNewBrandsC1 ||
    selectedCategory.isNewProductsC1 ||
    selectedCategory.urlId.id === EXCLUSIVES_CATEGORY_URL_ID;

  if (showNewLeftCategoryNavigation) {
    return buildLeftNavLinksForNewProductsAndNewBrands(
      categories,
      routingContext
    );
  } else {
    return buildLeftNavLinksForProductBasedCategory(
      categories,
      selectedCategory,
      routingContext
    );
  }
};

/*
 * Builds the left links for a product based category (any non-featured, non-new category)
 */
const buildLeftNavLinksForProductBasedCategory = (
  categories: ICategory[],
  selectedCategory: Category,
  routingContext: IRoutingContext
): ILeftNavLink[] => {
  const c1Category = categories.find(
    (category) =>
      category.is_product_based && category.url_id === selectedCategory.urlId.id
  );
  if (c1Category === undefined) {
    return [];
  }

  const leftNavLink = buildLeftNavLinkForCategory(
    c1Category,
    [],
    routingContext
  );
  if (leftNavLink === undefined) {
    return [];
  }
  return [leftNavLink];
};

// Note: the New Brand + Product categories are unique from the other categories, as they
// display 2 sets of SideNav links instead of just the usual 1.
// As a result, the returned ILeftNavLink[] value will contain 2 ILeftNavLinks, instead of
// the usual 1.
const buildLeftNavLinksForNewProductsAndNewBrands = (
  categories: ICategory[],
  routingContext: IRoutingContext
): ILeftNavLink[] => {
  const newBrandsCategory = categories.find(
    (category) => category.url_id === NEW_BRANDS_CATEGORY_URL_ID
  );
  const newProductsCategory = categories.find(
    (category) => category.url_id === NEW_PRODUCTS_CATEGORY_URL_ID
  );

  const newNavLinks = [];

  if (newBrandsCategory !== undefined) {
    const newBrandsNavLink = buildLeftNavLinkForCategory(
      newBrandsCategory,
      [],
      routingContext
    );
    const insiderCategory = getInsiderEarlyAccessCategory(categories);

    if (newBrandsNavLink && insiderCategory) {
      const insiderCategoryNavLink = buildLeftNavLinkForCategory(
        insiderCategory,
        [],
        routingContext
      );

      if (insiderCategoryNavLink) {
        newBrandsNavLink.childNavLinks.unshift(insiderCategoryNavLink);
      }
    }

    newNavLinks.push(newBrandsNavLink);
  }

  if (newProductsCategory !== undefined) {
    newNavLinks.push({
      name: newProductsCategory.name ?? "",
      to: categoryPath({
        urlIds: [
          { id: NEW_PRODUCTS_CATEGORY_URL_ID },
          categoryToUrlId(newProductsCategory.sub_categories[0]),
        ],
        routingContext,
      }),
      identifier: newProductsCategory.path_identifier,
      childNavLinks: newProductsCategory.sub_categories
        .map((category) =>
          buildLeftNavLinkForCategory(
            category,
            [{ id: NEW_PRODUCTS_CATEGORY_URL_ID }],
            routingContext
          )
        )
        .filter(isNotUndefined),
    });
  }

  return newNavLinks.filter(isNotUndefined);
};

const buildLeftNavLinkForCategory = (
  category: ICategory,
  parentUrlIds: IUrlId[],
  routingContext: IRoutingContext
): ILeftNavLink | undefined => {
  const categoryUrlId = { id: category.url_id ?? "" };

  return {
    name: category.name ?? "",
    identifier: category.path_identifier ?? "",
    to: categoryPath({
      urlIds: [...parentUrlIds, categoryUrlId],
      routingContext,
    }),
    childNavLinks: category.sub_categories
      .map((childCategory) => {
        return buildLeftNavLinkForCategory(
          childCategory,
          [...parentUrlIds, categoryUrlId],
          routingContext
        );
      })
      .filter(isNotUndefined),
  };
};

const getInsiderEarlyAccessCategory = (
  categories: ICategory[]
): ICategory | undefined => {
  return categories.find(
    (category) => category.url_id === EXCLUSIVES_CATEGORY_URL_ID
  );
};
